import { useState, useRef, useEffect } from "react";
import { MessageCircleQuestion, X, Send, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { router, usePage } from "@inertiajs/react";

type Status = "idle" | "sending" | "sent" | "error";

export function SupportButton() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const panelRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (
        panelRef.current?.contains(e.target as Node) ||
        buttonRef.current?.contains(e.target as Node)
      ) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const { currentUser, orgSlug } = usePage<{
    currentUser: { name: string; email: string } | null;
    orgSlug: string;
  }>().props;

  useEffect(() => {
    if (open && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [open]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim()) return;

    setStatus("sending");

    fetch(`/${orgSlug}/cs_support`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token":
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content") || "",
      },
      body: JSON.stringify({ subject, message }),
    })
      .then((res) => {
        if (res.ok) {
          setStatus("sent");
          setTimeout(() => {
            setOpen(false);
            setStatus("idle");
            setMessage("");
            setSubject("");
          }, 2000);
        } else {
          setStatus("error");
        }
      })
      .catch(() => setStatus("error"));
  }

  return (
    <>
      {/* Floating button */}
      <button
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        className={cn(
          "fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-all duration-200 hover:scale-105",
          open
            ? "bg-muted text-muted-foreground"
            : "bg-primary text-primary-foreground",
        )}
      >
        {open ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircleQuestion className="h-6 w-6" />
        )}
      </button>

      {/* Panel */}
      {open && (
        <div ref={panelRef} className="fixed bottom-24 right-6 z-50 w-80 rounded-2xl border border-border bg-card shadow-xl">
          {status === "sent" ? (
            <div className="flex flex-col items-center gap-3 p-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
                <Check className="h-6 w-6" />
              </div>
              <p className="text-sm font-medium text-foreground">
                ¡Mensaje enviado!
              </p>
              <p className="text-center text-xs text-muted-foreground">
                Te responderemos lo antes posible.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col">
              <div className="border-b border-border px-5 py-4">
                <h3 className="text-sm font-semibold text-foreground">
                  ¿Necesitas ayuda?
                </h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Envianos un mensaje y te contactaremos pronto por WhatsApp.
                </p>
              </div>

              <div className="flex flex-col gap-3 p-5">
                <input
                  type="text"
                  placeholder="Asunto (opcional)"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <textarea
                  ref={textareaRef}
                  placeholder="Describe tu problema o pregunta..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  className="resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                />
                {status === "error" && (
                  <p className="text-xs text-destructive">
                    Error al enviar. Intentá de nuevo.
                  </p>
                )}
              </div>

              <div className="border-t border-border px-5 py-3">
                <button
                  type="submit"
                  disabled={status === "sending" || !message.trim()}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  {status === "sending" ? "Enviando..." : "Enviar mensaje"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </>
  );
}
